/**
 * Accounts view (task 21.2): account list with balance + paginated
 * transactions, and per-transaction reversal.
 *
 * - Lists every owned account with its type and current balance (R4.2, R4.3),
 *   letting the customer select one.
 * - For the selected account, shows balance + currency (R2.1) and the latest
 *   transactions, cursor-paginated with a "Load more" control (R2.2, R2.3),
 *   each row showing amount, currency, date, status, and category (R2.4).
 * - Offers a Reverse action on eligible rows (R6.1); the resulting envelope
 *   (`REVERSAL_OUT_OF_WINDOW`, `ALREADY_REVERSED`, `NOT_AUTHORIZED`) renders
 *   inline and a success refreshes the account balance and the listing.
 *
 * Presentation redesign: this view is now a bank dashboard — a balance hero,
 * an account selector rendered as clickable radio cards (replacing the old
 * `<select>`), a quick-actions row, and a modern transaction feed (a role=list
 * of rows with category icons) replacing the plain table. The data fetched, the
 * selection state (`selectedId`/`setSelectedId`), the pagination, and the
 * reversal handler are all unchanged; only markup/classNames changed. Account
 * selection still drives the exact same `setSelectedId` state as before.
 *
 * Feed rows read like a bank statement: the primary line
 * ({@link transactionPrimaryLine}, e.g. "Payment to Byron Snyman" / "Transfer
 * to Savings", falling back to a derived description when no counterparty is
 * stored), and the customer's own payment reference — when present
 * ({@link transactionReference}) — shows as a smaller, muted second line beneath
 * it. Alongside are the category as a small chip and a natural
 * time-free date ({@link formatRelativeDate}); rows are grouped under
 * "Today" / "Yesterday" / date headers. The Reverse action is offered only for
 * posted rows still inside the 3-day window ({@link isReversibleNow}, a
 * display-only mirror of R6.2 — the server remains authoritative); older posted
 * rows are greyed with "No longer reversible". Clicking Reverse opens the
 * shared inline {@link ConfirmAction} panel and only Confirm issues the call.
 *
 * All service calls go through the shared API client; failures render the
 * standard error envelope via {@link ErrorNotice}. Markup uses semantic
 * regions, accessible radio cards, and live regions for accessibility.
 */

import { useCallback, useEffect, useId, useState } from 'react';

import { accountsApi, reversalApi, type Account, type Transaction } from '../api/index.js';
import { ConfirmAction } from '../ui/ConfirmAction.js';
import { ErrorNotice } from '../ui/ErrorNotice.js';
import {
  categoryLabel,
  formatMoney,
  formatRelativeDate,
  formatShortDate,
  isReversibleNow,
  transactionPrimaryLine,
  transactionReference,
  transactionTitle,
} from '../ui/format.js';
import {
  IconIncome,
  IconPlus,
  IconTransfer,
} from '../ui/BrandLogo.js';
import { categoryVisual } from '../ui/categoryVisual.js';
import { useAction, useAsyncData } from '../ui/useAsync.js';

/** Sections the dashboard quick actions can navigate to (matches App shell). */
type SectionId =
  | 'accounts'
  | 'beneficiaries'
  | 'transfers'
  | 'insights'
  | 'reversal'
  | 'notifications';

/** Whether a posted row has aged out of the client-side reversal window. */
function isExpiredPosted(tx: Transaction): boolean {
  return tx.status.toLowerCase() === 'posted' && !isReversibleNow(tx);
}

/**
 * Group consecutive rows (newest first, as served) by their natural date label
 * so the feed can render "Today" / "Yesterday" / "25 Sept" headers.
 */
function groupByDate(
  transactions: readonly Transaction[],
): readonly { readonly label: string; readonly rows: readonly Transaction[] }[] {
  const groups: { label: string; rows: Transaction[] }[] = [];
  for (const tx of transactions) {
    const label = formatRelativeDate(tx.transactionDate);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.label === label) {
      last.rows.push(tx);
    } else {
      groups.push({ label, rows: [tx] });
    }
  }
  return groups;
}

/** Last four characters of an account id, for a masked reference display. */
function maskedRef(accountId: string): string {
  const tail = accountId.slice(-4);
  return `•••• ${tail}`;
}

/** Whether the formatted money string represents a credit (non-negative). */
function isCredit(amount: string, currency: string): boolean {
  return !formatMoney(amount, currency).trim().startsWith('-');
}

/** The transactions feed for one account, with cursor pagination + reversal. */
function TransactionList({
  account,
  onBalanceMayHaveChanged,
}: {
  readonly account: Account;
  readonly onBalanceMayHaveChanged: () => void;
}): JSX.Element {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [initialised, setInitialised] = useState(false);
  /** The row currently awaiting confirmation in the inline confirm panel. */
  const [pendingReverseId, setPendingReverseId] = useState<string | undefined>(undefined);

  const loadPage = useCallback(
    async (nextCursor: string | undefined, append: boolean) => {
      setLoading(true);
      setError(undefined);
      try {
        const page = await accountsApi.getTransactions(account.accountId, nextCursor);
        setTransactions((prev) =>
          append ? [...prev, ...page.transactions] : [...page.transactions],
        );
        setCursor(page.nextCursor);
        setInitialised(true);
      } catch (caught) {
        setError(caught);
      } finally {
        setLoading(false);
      }
    },
    [account.accountId],
  );

  // Reset and load the first page whenever the selected account changes.
  useEffect(() => {
    setTransactions([]);
    setCursor(null);
    setInitialised(false);
    setPendingReverseId(undefined);
    void loadPage(undefined, false);
  }, [loadPage]);

  const reverse = useAction(async (transactionId: string) => {
    await reversalApi.reverseTransaction(transactionId);
  });

  async function onReverse(transactionId: string): Promise<void> {
    const result = await reverse.run(transactionId);
    if (result !== undefined) {
      setPendingReverseId(undefined);
      onBalanceMayHaveChanged();
      await loadPage(undefined, false);
    }
  }

  const groups = groupByDate(transactions);

  return (
    <section aria-labelledby="transactions-heading" className="esb-card esb-feed-card">
      <div className="esb-feed-card__head">
        <h3 id="transactions-heading" className="esb-subhead">Transactions</h3>
        <span className="esb-feed-card__acct">{account.accountType} · {maskedRef(account.accountId)}</span>
      </div>

      <ErrorNotice error={error} />
      <ErrorNotice error={reverse.error} />

      {initialised && transactions.length === 0 && !loading ? (
        <p className="esb-empty">No transactions yet.</p>
      ) : (
        <div
          className="esb-feed-scroll"
          role="group"
          aria-label={`Transactions for account ${account.accountId}`}
        >
          {groups.map((group) => (
            <section key={group.label} className="esb-feed__section" aria-label={group.label}>
              <h4 className="esb-feed__group">{group.label}</h4>
              <ul className="esb-feed" role="list">
                {group.rows.map((tx) => {
                  const label = categoryLabel(tx.category);
                  const { Icon, tone } = categoryVisual(label);
                  const credit = isCredit(tx.amount, tx.currency);
                  const status = tx.status.toLowerCase();
                  const reversible = isReversibleNow(tx);
                  const expired = isExpiredPosted(tx);
                  const isPending = pendingReverseId === tx.transactionId;
                  return (
                    <li
                      key={tx.transactionId}
                      className={`esb-feed__row${expired ? ' esb-feed__row--expired' : ''}`}
                      role="listitem"
                    >
                      <span className={`esb-cat esb-cat--${tone}`} aria-hidden="true">
                        <Icon className="esb-cat__icon" />
                      </span>
                      <span className="esb-feed__body">
                        <span className="esb-feed__desc">{transactionPrimaryLine(tx)}</span>
                        {transactionReference(tx) !== null ? (
                          <span className="esb-feed__ref">{transactionReference(tx)}</span>
                        ) : null}
                        <span className="esb-feed__meta">
                          <span className="esb-feed__date">{formatRelativeDate(tx.transactionDate)}</span>
                          <span className="esb-chip">{label}</span>
                        </span>
                      </span>
                      <span className="esb-feed__right">
                        <span
                          className={credit ? 'esb-amount--pos' : 'esb-amount--neg'}
                        >
                          {formatMoney(tx.amount, tx.currency)}
                        </span>
                        {status !== 'posted' ? (
                          <span className={`esb-badge esb-badge--${status}`}>{tx.status}</span>
                        ) : null}
                      </span>
                      <span className="esb-feed__action">
                        {reversible && !isPending ? (
                          <button
                            type="button"
                            className="esb-btn esb-btn--ghost esb-btn--sm"
                            onClick={() => setPendingReverseId(tx.transactionId)}
                            disabled={reverse.pending}
                          >
                            Reverse
                          </button>
                        ) : null}
                        {expired ? (
                          <span className="esb-feed__expired">No longer reversible</span>
                        ) : null}
                      </span>
                      {isPending ? (
                        <div className="esb-feed__confirm">
                          <ConfirmAction
                            title={`Reverse ${formatMoney(tx.amount, tx.currency)} · ${transactionTitle(tx)} · ${formatShortDate(tx.transactionDate)}?`}
                            confirmLabel="Confirm reversal"
                            pendingLabel="Reversing…"
                            pending={reverse.pending}
                            tone="danger"
                            onConfirm={() => void onReverse(tx.transactionId)}
                            onCancel={() => setPendingReverseId(undefined)}
                          >
                            <p className="esb-confirm__text">
                              Transactions can be reversed within 3 days of the transaction date.
                              This cannot be undone.
                            </p>
                          </ConfirmAction>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      <div aria-live="polite">
        {loading ? <p className="esb-loading">Loading transactions…</p> : null}
      </div>

      {cursor !== null ? (
        <button
          type="button"
          className="esb-btn esb-btn--ghost esb-btn--sm"
          onClick={() => void loadPage(cursor, true)}
          disabled={loading}
        >
          Load more
        </button>
      ) : null}
    </section>
  );
}

/** Account dashboard: balance hero + account selector cards + quick actions +
 * transactions feed. `onNavigate` (optional) lets quick actions switch section. */
export function AccountsView({
  onNavigate,
}: {
  readonly onNavigate?: (section: SectionId) => void;
} = {}): JSX.Element {
  const accounts = useAsyncData(() => accountsApi.listAccounts(), []);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const groupId = useId();

  const list = accounts.data?.accounts ?? [];
  const selected =
    list.find((a) => a.accountId === selectedId) ?? list[0] ?? undefined;

  return (
    <section aria-labelledby="accounts-heading" className="esb-dashboard">
      <h2 id="accounts-heading" className="esb-section-title">Dashboard</h2>

      <ErrorNotice error={accounts.error} />

      <div aria-live="polite">
        {accounts.loading ? <p className="esb-loading">Loading accounts…</p> : null}
      </div>

      {!accounts.loading && list.length === 0 && accounts.error === undefined ? (
        <p className="esb-empty">You have no accounts.</p>
      ) : null}

      {list.length > 0 && selected !== undefined ? (
        <>
          {/* Balance hero — the selected account's balance shown large. */}
          <section aria-labelledby="balance-heading" className="esb-hero">
            <div className="esb-hero__top">
              <span className="esb-hero__type">{selected.accountType}</span>
              <span className="esb-hero__ref">{maskedRef(selected.accountId)}</span>
            </div>
            <h3 id="balance-heading" className="visually-hidden">Balance</h3>
            <p className="esb-hero__balance">{formatMoney(selected.balance, selected.currency)}</p>
            <p className="esb-hero__meta">Available balance · {selected.currency}</p>
          </section>

          {/* Account selector — clickable radio cards replacing the <select>. */}
          <div
            className="esb-acct-cards"
            role="radiogroup"
            aria-label="Select account"
            id={groupId}
          >
            {list.map((account) => {
              const isSel = account.accountId === selected.accountId;
              return (
                <button
                  key={account.accountId}
                  type="button"
                  role="radio"
                  aria-checked={isSel}
                  className={`esb-acct-card${isSel ? ' esb-acct-card--active' : ''}`}
                  onClick={() => setSelectedId(account.accountId)}
                >
                  <span className="esb-acct-card__type">{account.accountType}</span>
                  <span className="esb-acct-card__ref">{maskedRef(account.accountId)}</span>
                  <span className="esb-acct-card__balance">
                    {formatMoney(account.balance, account.currency)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Quick actions — fintech action tiles that switch section. */}
          <div className="esb-quick" aria-label="Quick actions">
            <button
              type="button"
              className="esb-quick__tile"
              onClick={() => onNavigate?.('transfers')}
            >
              <span className="esb-quick__icon"><IconTransfer /></span>
              <span className="esb-quick__label">Transfer</span>
            </button>
            <button
              type="button"
              className="esb-quick__tile"
              onClick={() => onNavigate?.('transfers')}
            >
              <span className="esb-quick__icon"><IconIncome /></span>
              <span className="esb-quick__label">Pay beneficiary</span>
            </button>
            <button
              type="button"
              className="esb-quick__tile"
              onClick={() => onNavigate?.('beneficiaries')}
            >
              <span className="esb-quick__icon"><IconPlus /></span>
              <span className="esb-quick__label">Add beneficiary</span>
            </button>
          </div>

          <TransactionList
            account={selected}
            onBalanceMayHaveChanged={accounts.reload}
          />
        </>
      ) : null}
    </section>
  );
}
